/**
 * MUI Locale Provider
 * Synchronizes MUI components with the current i18n language
 */

import React, { createContext, useContext, useEffect, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { enUS, roRO } from '@mui/material/locale';
import { useTranslation } from 'react-i18next';
import { theme as baseTheme } from '../theme.config';

/**
 * Available MUI locales mapped to language codes
 */
const MUI_LOCALES: Record<string, any> = {
    en: enUS,
    ro: roRO,
};

/**
 * MUI Locale context interface
 */
interface MuiLocaleContextType {
    currentLocale: typeof enUS;
    setLocale: (locale: string) => void;
}

const MuiLocaleContext = createContext<MuiLocaleContextType | undefined>(undefined);

/**
 * Props for MUI Locale Provider
 */
interface MuiLocaleProviderProps {
    children: React.ReactNode;
}

/**
 * MUI Locale Provider component
 * Provides MUI localization that syncs with i18next language changes
 *
 * @param props - The component props
 * @returns JSX element providing MUI locale context
 */
export function MuiLocaleProvider({ children }: MuiLocaleProviderProps) {
    const { i18n } = useTranslation();
    const [currentLocale, setCurrentLocale] = useState<typeof enUS>(MUI_LOCALES[i18n.language] || enUS);

    /**
     * Update MUI locale when i18n language changes
     */
    useEffect(() => {
        const handleLanguageChanged = (lng: string) => {
            const newLocale = MUI_LOCALES[lng] || enUS;
            setCurrentLocale(newLocale);
        };

        // Set initial locale
        handleLanguageChanged(i18n.language);

        // Listen for language changes
        i18n.on('languageChanged', handleLanguageChanged);

        return () => {
            i18n.off('languageChanged', handleLanguageChanged);
        };
    }, [i18n]);

    /**
     * Set locale manually (for programmatic control)
     */
    const setLocale = (locale: string) => {
        const newLocale = MUI_LOCALES[locale] || enUS;
        setCurrentLocale(newLocale);
        // This will also trigger i18n language change if needed
        if (i18n.language !== locale) {
            i18n.changeLanguage(locale);
        }
    };

    /**
     * Create theme with current locale
     */
    const localizedTheme = createTheme(baseTheme, currentLocale);

    const contextValue: MuiLocaleContextType = {
        currentLocale,
        setLocale,
    };

    return (
        <MuiLocaleContext.Provider value={contextValue}>
            <ThemeProvider theme={localizedTheme}>
                {children}
            </ThemeProvider>
        </MuiLocaleContext.Provider>
    );
}

/**
 * Hook to use MUI locale context
 *
 * @returns MUI locale context value
 * @throws Error if used outside of MuiLocaleProvider
 */
export function useMuiLocale(): MuiLocaleContextType {
    const context = useContext(MuiLocaleContext);
    if (context === undefined) {
        throw new Error('useMuiLocale must be used within a MuiLocaleProvider');
    }
    return context;
}

/**
 * Hook to get current MUI locale
 *
 * @returns Current MUI locale object
 */
export function useCurrentMuiLocale(): typeof enUS {
    const { currentLocale } = useMuiLocale();
    return currentLocale;
}
